import React, { useEffect, useState, useRef, useCallback } from 'react';
import { socket } from '../../services/socket';
import { useAuth } from '../../context/AuthContext';
import { Send, MessageCircle, AlertCircle, Video } from 'lucide-react';
import { debugLog, debugError } from '../../utils/logger';
import appointmentService from '../../services/appointmentService';

function ChatBox({ appointment, otherUser, onOpenVideoCall }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [connected, setConnected] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  debugLog('\n💬 [ChatBox] Component rendered');
  debugLog('📊 [ChatBox] Appointment ID:', appointment?._id);
  debugLog('📊 [ChatBox] Other user:', otherUser?.user?.profile?.firstName);
  debugLog('📊 [ChatBox] Socket connected:', socket.connected);
  debugLog('📊 [ChatBox] Messages count:', messages.length);

  const getUserId = useCallback(() => {
    if (user?._id) return user._id;
    if (user?.id) return user.id;

    try {
      const token = localStorage.getItem('token');
      if (token) {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split('')
            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        );
        const decoded = JSON.parse(jsonPayload);
        return decoded.userId || decoded.id || decoded._id;
      }
    } catch (error) {
      debugError('❌ [getUserId] Error decodificando token:', error);
    }

    return null;
  }, [user]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Cargar historial persistido de la cita (hasta la purga diaria de 11:59 p. m.)
  // y fusionarlo con los mensajes en vivo. Si falla (p. ej. sesión limitada de
  // WhatsApp sin acceso REST), el chat en vivo sigue funcionando.
  useEffect(() => {
    if (!appointment?._id || !user) return;

    let cancelled = false;
    const myId = getUserId();
    const myName = `${user.profile?.firstName || 'Usuario'} ${user.profile?.lastName || ''}`.trim();
    const otherName = `${otherUser?.user?.profile?.firstName || ''} ${otherUser?.user?.profile?.lastName || ''}`.trim() || 'Otro usuario';

    appointmentService
      .getChatHistory(appointment._id)
      .then((res) => {
        if (cancelled || !res?.data) return;
        const history = res.data.map((m) => {
          const ts = new Date(m.createdAt).getTime();
          const isMine = String(m.senderId) === String(myId);
          return {
            from: m.senderId,
            fromName: isMine ? myName : otherName,
            text: m.content,
            timestamp: ts,
            appointmentId: m.appointmentId
          };
        });

        setMessages((prev) => {
          const merged = [...history];
          prev.forEach((msg) => {
            const dup = merged.some((h) => h.timestamp === msg.timestamp && String(h.from) === String(msg.from));
            if (!dup) merged.push(msg);
          });
          merged.sort((a, b) => a.timestamp - b.timestamp);
          return merged;
        });
      })
      .catch((err) => {
        debugError('❌ [ChatBox] No se pudo cargar el historial de chat:', err?.message);
      });

    return () => { cancelled = true; };
  }, [appointment, user, otherUser, getUserId]);

  useEffect(() => {
    if (!appointment || !user) {
      debugLog('⚠️ [ChatBox useEffect] Missing appointment or user');
      return;
    }

    const userId = getUserId();
    debugLog('\n🔄 [ChatBox useEffect] Initializing chat');
    debugLog('📊 [useEffect] Appointment ID:', appointment._id);
    debugLog('📊 [useEffect] User ID:', userId);
    debugLog('📊 [useEffect] Socket connected:', socket.connected);
    debugLog('📊 [useEffect] Socket ID:', socket.id);

    if (socket.connected) {
      setConnected(true);
      debugLog('✅ [useEffect] Socket already connected');
    } else {
      debugLog('🔌 [useEffect] Socket not connected, connecting...');
      socket.connect();
    }

    const userName = `${user.profile?.firstName || 'Usuario'} ${user.profile?.lastName || ''}`.trim();
    const userRole = user.role || 'patient';

    debugLog('📡 [useEffect] Emitiendo chat:join con presencia');
    debugLog('📊 User Name:', userName);
    debugLog('📊 User Role:', userRole);

    socket.emit('chat:join', {
      appointmentId: appointment._id,
      userId: userId,
      userName: userName,
      userRole: userRole
    });

    const handleConnect = () => {
      debugLog('\n✅ [ChatBox handleConnect] Socket connected');
      setConnected(true);
      debugLog('📤 [handleConnect] Re-emitting chat:join');
      socket.emit('chat:join', {
        appointmentId: appointment._id,
        userId: userId,
        userName: userName,
        userRole: userRole
      });
    };

    const handleDisconnect = () => {
      debugLog('\n❌ [ChatBox handleDisconnect] Socket disconnected');
      setConnected(false);
    };

    const handleChatMessage = (message) => {
      debugLog('\n📨 [handleChatMessage] Mensaje recibido:', message);
      debugLog('📊 [handleChatMessage] From:', message.senderId);
      debugLog('📊 [handleChatMessage] Content:', message.content);
      debugLog('📊 [handleChatMessage] Timestamp:', message.timestamp);

      const formattedMessage = {
        from: message.senderId,
        fromName: message.senderName || 'Usuario',
        text: message.content,
        timestamp: message.timestamp || Date.now(),
        appointmentId: message.appointmentId
      };

      setMessages((prev) => {
        const isDuplicate = prev.some(
          m => m.timestamp === formattedMessage.timestamp && m.from === formattedMessage.from
        );
        if (isDuplicate) {
          debugLog('⚠️ [handleChatMessage] Mensaje duplicado, ignorando');
          return prev;
        }
        debugLog('✅ [handleChatMessage] Agregando mensaje a la lista');
        return [...prev, formattedMessage];
      });
    };

    const handleChatTyping = (data) => {
      debugLog('\n⌨️ [handleChatTyping] Typing event:', data);
      debugLog('📊 [handleChatTyping] User ID:', data.userId);
      debugLog('📊 [handleChatTyping] Is typing:', data.isTyping);
      debugLog('📊 [handleChatTyping] My user ID:', userId);

      if (data.userId !== userId) {
        debugLog('✅ [handleChatTyping] Setting typing state:', data.isTyping);
        setIsTyping(data.isTyping);
      } else {
        debugLog('⚠️ [handleChatTyping] Ignoring own typing event');
      }
    };

    const handleChatError = (error) => {
      debugError('\n❌ [handleChatError] Chat error:', error);
    };

    debugLog('📡 [useEffect] Registering socket listeners');
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('chat:message', handleChatMessage);
    socket.on('chat:typing', handleChatTyping);
    socket.on('chat:error', handleChatError);
    debugLog('✅ [useEffect] All listeners registered');

    return () => {
      debugLog('\n🧹 [ChatBox cleanup] Cleaning up chat');
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('chat:message', handleChatMessage);
      socket.off('chat:typing', handleChatTyping);
      socket.off('chat:error', handleChatError);

      debugLog('📤 [cleanup] Emitting chat:leave for:', appointment._id);
      socket.emit('chat:leave', appointment._id);
      debugLog('✅ [cleanup] Chat cleanup complete');
    };
  }, [appointment, user, getUserId]);

  const handleSendMessage = (e) => {
    e.preventDefault();

    debugLog('\n📤 [handleSendMessage] Attempting to send message');

    if (!inputMessage.trim()) {
      debugLog('⚠️ [handleSendMessage] Empty message, aborting');
      return;
    }

    const userId = getUserId();
    if (!userId) {
      debugError('❌ [handleSendMessage] No user ID available');
      alert('Error: No se puede enviar mensaje. Intenta cerrar sesión y volver a entrar.');
      return;
    }

    if (!socket.connected) {
      debugError('❌ [handleSendMessage] Socket not connected');
      alert('Error: No hay conexión. Espera un momento e intenta de nuevo.');
      return;
    }

    const messageData = {
      appointmentId: appointment._id,
      senderId: userId,
      senderName: `${user?.profile?.firstName || 'Usuario'} ${user?.profile?.lastName || ''}`.trim(),
      content: inputMessage.trim(),
      timestamp: Date.now()
    };

    debugLog('📊 [handleSendMessage] Message data:', messageData);
    debugLog('📊 [handleSendMessage] Socket ID:', socket.id);
    debugLog('📊 [handleSendMessage] Socket connected:', socket.connected);

    debugLog('📤 [handleSendMessage] Emitting chat:message');
    socket.emit('chat:message', messageData);
    debugLog('✅ [handleSendMessage] Message emitted');

    setInputMessage('');

    debugLog('📤 [handleSendMessage] Emitting chat:typing (false)');
    socket.emit('chat:typing', {
      appointmentId: appointment._id,
      userId: userId,
      isTyping: false
    });
  };

  const handleTyping = () => {
    const userId = getUserId();
    if (!userId) {
      debugLog('⚠️ [handleTyping] No user ID available');
      return;
    }

    if (!socket.connected) {
      debugLog('⚠️ [handleTyping] Socket not connected');
      return;
    }

    debugLog('⌨️ [handleTyping] User is typing');

    socket.emit('chat:typing', {
      appointmentId: appointment._id,
      userId: userId,
      isTyping: true
    });

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      debugLog('⌨️ [handleTyping] Typing timeout, setting to false');
      socket.emit('chat:typing', {
        appointmentId: appointment._id,
        userId: userId,
        isTyping: false
      });
    }, 2000);
  };

  if (!appointment || !otherUser) {
    debugLog('⚠️ [ChatBox] Missing appointment or otherUser, showing placeholder');
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <MessageCircle className="h-12 w-12 mr-3" />
        <span>Selecciona una cita para chatear</span>
      </div>
    );
  }

  const myUserId = getUserId();

  return (
    <div className="flex flex-col h-full bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="bg-primary-600 text-white p-4 rounded-t-lg flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-semibold truncate">
            Chat con {otherUser.user?.profile?.firstName} {otherUser.user?.profile?.lastName}
          </h3>
          <p className="text-xs text-primary-100">
            {otherUser.specialization || 'Paciente'}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="flex items-center">
            <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-400' : 'bg-red-400'} mr-2`}></div>
            <span className="text-xs">{connected ? 'Conectado' : 'Desconectado'}</span>
          </div>
          {onOpenVideoCall && (
            <button
              type="button"
              onClick={onOpenVideoCall}
              title="Pasar a videollamada"
              className="flex items-center gap-1 bg-white/15 hover:bg-white/25 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition"
            >
              <Video className="h-4 w-4" />
              <span className="hidden sm:inline">Video</span>
            </button>
          )}
        </div>
      </div>

      {/* Advertencia */}
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 text-sm">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
          <p className="text-yellow-800">
            <strong>Nota:</strong> Los mensajes de este chat son privados entre el médico y el paciente.
            Se borran automáticamente todos los días a las 11:59 p. m.
          </p>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50" style={{ maxHeight: '400px' }}>
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <MessageCircle className="h-12 w-12 mb-2" />
            <span>No hay mensajes. ¡Inicia la conversación!</span>
          </div>
        ) : (
          messages.map((msg, idx) => {
            const isMe = String(msg.from) === String(myUserId);

            return (
              <div key={`${msg.timestamp}-${idx}`} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${isMe
                  ? 'bg-blue-600 text-white rounded-br-none shadow-lg'
                  : 'bg-white text-gray-800 rounded-bl-none shadow-md border-2 border-gray-300'
                  }`}>
                  {!isMe && (
                    <p className="text-xs font-semibold text-gray-600 mb-1">
                      {msg.fromName || 'Otro usuario'}
                    </p>
                  )}

                  <p className="break-words text-sm">{msg.text}</p>

                  <p className={`text-xs mt-1 ${isMe ? 'text-blue-100' : 'text-gray-500'}`}>
                    {new Date(msg.timestamp).toLocaleTimeString('es-CO', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
            );
          })
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area con Indicador de Escritura */}
      <div className="bg-white border-t">
        {isTyping && (
          <div className="px-4 pt-3 pb-1">
            <div className="inline-flex items-center bg-gray-100 text-gray-600 px-3 py-2 rounded-full text-xs">
              <div className="flex space-x-1 mr-2">
                <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
              <span className="italic font-medium">Escribiendo...</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSendMessage} className="p-4">
          <div className="flex gap-2">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => {
                setInputMessage(e.target.value);
                handleTyping();
              }}
              placeholder="Escribe un mensaje..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={!connected}
            />
            <button
              type="submit"
              disabled={!connected || !inputMessage.trim()}
              className="btn-primary px-6 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="h-5 w-5" />
            </button>
          </div>
          {!connected && (
            <p className="text-xs text-red-500 mt-2">Reconectando...</p>
          )}
        </form>
      </div>
    </div>
  );
}

export default ChatBox;
